import React, { useEffect, useMemo, useState } from 'react';
import { Archive, ArrowCounterClockwise, CalendarBlank, Check, CircleNotch, FolderSimple, Sparkle, WarningCircle, X } from '@phosphor-icons/react';
import { ImmoButton, ObjektPicker, TextFeld, type ObjektPickerOption } from '../../../../components/immobilien/ui';
import { flacheOrdner } from '../../../../lib/dokumente/aktenschrank';
import { formatiereGroesse } from '../../../../lib/dokumente/aktenschrankApi';
import { mitObjektnummer } from '../../../../lib/dokumente/dateiname';
import type { AktObjekt } from '../../../../lib/dokumente/useAktenschrank';
import {
  ALTARCHIV_ORDNER, bezuege, innererPfad, jahrAusText, ohneDatumImNamen, pruefeAblage, setzeDatumImNamen, type AblageWerte, type PruefZeile,
} from '../../../../lib/dokumente/altarchivPruefung';
import { dateiDarstellung, deDatumZeit } from '../aktenschrank/dateiDarstellung';
import { OrdnerAuswahl } from '../ablage/OrdnerAuswahl';
import type { OrdnerQuelle } from '../ablage/ordnerQuelle';
import { PruefVorschau } from './PruefVorschau';

export interface PruefAktionen {
  onUebernehmen: (z: PruefZeile, w: AblageWerte) => Promise<void>;
  onAblehnen: (z: PruefZeile) => Promise<void>;
  onZurueck: (z: PruefZeile) => Promise<void>;
  onOeffnen: (z: PruefZeile) => void;
}

const ton = (s: number) => (s >= 0.9 ? 'var(--ok)' : s >= 0.7 ? 'var(--warn)' : 'var(--bad)');
const stufe = (s: number) => (s >= 0.9 ? 'sehr sicher' : s >= 0.7 ? 'eher sicher' : 'unsicher');
const deDatum = (iso: string | null) => (iso ? iso.split('-').reverse().join('.') : null);
// Ordner, die in der Auswahl nichts zu suchen haben (der Altarchiv-Ordner selbst und das alte Archiv).
const AUSGEBLENDET = new RegExp(`^(${ALTARCHIV_ORDNER}|99 Archiv)(/|$)`);

/** Karte je Altarchiv-PDF: alter Ort, Vorschlag der KI, vor dem Übernehmen änderbar. */
export function PruefKarte({ z, objekte, pickerOptionen, ordner, aktionen, gesperrt, mobil }: {
  z: PruefZeile;
  objekte: readonly AktObjekt[];
  pickerOptionen: readonly ObjektPickerOption[];
  ordner: OrdnerQuelle;
  aktionen: PruefAktionen;
  gesperrt: boolean;
  mobil: boolean;
}) {
  const alterName = z.dokument?.datei_name || 'Datei';
  const d = dateiDarstellung(alterName);
  const sicherheit = z.sicherheit ?? 0;
  const offen = z.status === 'vorschlag' || z.status === 'ohne_objekt';

  const [objektId, setObjektId] = useState<string | null>(z.objekt_id);
  const [inner, setInner] = useState(innererPfad(z.ziel_pfad));
  const [dateiname, setDateiname] = useState(z.neuer_name ?? alterName);
  // Ohne Datum im Inhalt: Kalenderfeld, das Datum wird vorne in den Namen gesetzt (Jahr aus altem Ordner als Hinweis).
  const ohneDatum = ohneDatumImNamen(z.neuer_name);
  const [datum, setDatum] = useState('');
  const jahrHinweis = ohneDatum ? jahrAusText(`${z.alter_pfad ?? ''} ${alterName}`) : null;
  const waehleDatum = (iso: string) => { setDatum(iso); if (iso) setDateiname(n => setzeDatumImNamen(n, iso)); };
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const baum = ordner.liste(objektId);
  useEffect(() => { if (objektId) ordner.lade(objektId); }, [objektId, ordner]);
  const optionen = useMemo(() => {
    const flach = (baum ? flacheOrdner(baum) : []).filter(o => !AUSGEBLENDET.test(o.pfad));
    if (!inner || flach.some(o => o.pfad === inner)) return flach;
    return [{ pfad: inner, name: baum ? `${inner} (wird angelegt)` : inner, tiefe: 0 }, ...flach];
  }, [baum, inner]);

  const wechsleObjekt = (id: string) => {
    if (id === objektId) return;
    setObjektId(id);
    setDateiname(n => mitObjektnummer(n, objekte.find(o => o.id === id)?.nummer));
  };

  const hinweis = pruefeAblage({ dateiname, zielPfad: inner, objektId });
  const fuehreAus = async (f: () => Promise<void>) => {
    setLaeuft(true); setFehler(null);
    try { await f(); } catch (e) { setFehler(e instanceof Error ? e.message : 'Aktion fehlgeschlagen'); } finally { setLaeuft(false); }
  };
  const sperre = laeuft || gesperrt;
  const liste = bezuege(z);
  const objektText = z.objekt ? [z.objekt.objektnummer, z.objekt.bezeichnung].filter(Boolean).join(' · ') : null;

  return (
    <article className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 26, padding: 16, gap: 12 }} aria-label={alterName}>
      <div className="flex items-start" style={{ gap: 12 }}>
        <span className="flex items-center justify-center shrink-0" style={{ width: 46, height: 46, borderRadius: 14, background: d.bg }}><d.icon weight="fill" size={22} color={d.fg} /></span>
        <div className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
          <span style={{ fontSize: 15, fontWeight: 800, overflowWrap: 'anywhere' }}>{alterName}</span>
          <span className="flex items-start" style={{ gap: 5, fontSize: 12, color: 'var(--ink3)', overflowWrap: 'anywhere' }}>
            <FolderSimple size={13} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{(z.alter_pfad || 'alter Ordner unbekannt').split('/').join(' › ')}</span>
          </span>
          <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>
            {[formatiereGroesse(z.dokument?.datei_groesse != null ? String(z.dokument.datei_groesse) : null), objektText].filter(Boolean).join(' · ')}
          </span>
        </div>
      </div>

      <PruefVorschau dokumentId={z.dokument_id} dateiname={alterName} onedriveUrl={z.dokument?.onedrive_url ?? null} mobil={mobil} />

      {z.status !== 'ohne_objekt' && (
        <div className="immo-innen flex flex-col min-w-0" style={{ borderRadius: 16, padding: '10px 14px', gap: 6 }}>
          <span className="flex items-center flex-wrap" style={{ gap: 6, fontSize: 13, fontWeight: 700 }}>
            <Sparkle weight="fill" size={14} color="var(--acc)" />
            {[z.dokumentart, z.dokument_datum ? `${deDatum(z.dokument_datum)}${z.datum_quelle && z.datum_quelle !== 'Inhalt' ? ` (aus ${z.datum_quelle})` : ''}` : 'ohne Datum', z.absender].filter(Boolean).join(' · ')}
          </span>
          {liste.length > 0 && (
            <span className="flex flex-wrap" style={{ gap: 6 }}>
              {liste.map(b => (
                <span key={b.art} style={{ padding: '3px 9px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink2)' }}>{b.art}: {b.text}</span>
              ))}
            </span>
          )}
          {z.begruendung && <span style={{ fontSize: 12, color: 'var(--ink3)', lineHeight: 1.45 }}>{z.begruendung}</span>}
          <span className="flex items-center flex-wrap" style={{ gap: 10, fontSize: 11 }}>
            <span style={{ flex: '1 1 120px', height: 6, borderRadius: 3, background: 'var(--fill3)', overflow: 'hidden' }} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(sicherheit * 100)} aria-label="Sicherheit">
              <span style={{ display: 'block', height: '100%', width: `${Math.round(sicherheit * 100)}%`, background: ton(sicherheit) }} />
            </span>
            <span style={{ fontWeight: 800, color: ton(sicherheit) }}>{stufe(sicherheit)}</span>
          </span>
        </div>
      )}

      {offen && (
        <div className="immo-innen flex flex-col min-w-0" style={{ borderRadius: 20, padding: 14, gap: 12 }}>
          <div className="flex flex-col" style={{ gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Objekt</span>
            <div><ObjektPicker optionen={pickerOptionen} wert={objektId} onWahl={wechsleObjekt} /></div>
          </div>
          <div className="flex flex-col" style={{ gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Ordner im Altarchiv des Objekts</span>
            <OrdnerAuswahl optionen={optionen} wert={inner} onWahl={setInner} disabled={!objektId || sperre}
              platzhalter={objektId ? (baum ? 'Direkt in „Altarchiv“ – oder Ordner wählen …' : 'Ordner werden geladen …') : 'Zuerst ein Objekt wählen'} />
          </div>
          {ohneDatum && (
            <div className="flex flex-col" style={{ gap: 6 }}>
              <label htmlFor={`datum-${z.dokument_id}`} style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Datum des Dokuments (im Inhalt nicht erkannt)</label>
              <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
                <span className="flex items-center" style={{ gap: 8, height: 44, padding: '0 12px', borderRadius: 14, background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
                  <CalendarBlank size={16} color="var(--ink3)" aria-hidden />
                  <input id={`datum-${z.dokument_id}`} type="date" value={datum} max={new Date().toISOString().slice(0, 10)} disabled={sperre}
                    onChange={e => waehleDatum(e.target.value)}
                    style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 14, fontWeight: 700, color: 'var(--ink)', colorScheme: 'light dark' }} />
                </span>
                {jahrHinweis && !datum && (
                  <button type="button" disabled={sperre} onClick={() => waehleDatum(`${jahrHinweis}-01-01`)}
                    style={{ height: 44, padding: '0 12px', borderRadius: 14, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
                    Jahr laut altem Ordner: {jahrHinweis} – 01.01.{jahrHinweis} einsetzen
                  </button>
                )}
              </div>
            </div>
          )}
          <TextFeld label="Neuer Dateiname" wert={dateiname} onChange={setDateiname} kompakt disabled={sperre}
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, fontWeight: 700, color: 'var(--ink)', fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace" }} />
          {(fehler || hinweis) && (
            <span className="flex items-start" style={{ gap: 6, fontSize: 12, color: fehler ? 'var(--bad)' : 'var(--warn)' }}>
              <WarningCircle weight="fill" size={14} style={{ flexShrink: 0, marginTop: 1 }} />{fehler ?? hinweis}
            </span>
          )}
          <div className="flex justify-end flex-wrap" style={{ gap: 8 }}>
            <ImmoButton art="fuellung" groesse={44} icon={X} disabled={sperre} onClick={() => void fuehreAus(() => aktionen.onAblehnen(z))}>Ablehnen</ImmoButton>
            <ImmoButton art="akzent" groesse={44} icon={laeuft ? CircleNotch : Check} disabled={sperre || !!hinweis || !objektId}
              onClick={() => objektId && void fuehreAus(() => aktionen.onUebernehmen(z, { objektId, zielPfad: inner, dateiname }))}>
              {laeuft ? 'Wird übernommen …' : 'Übernehmen'}
            </ImmoButton>
          </div>
        </div>
      )}

      {z.status === 'abgelegt' && (
        <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
          <span className="flex-1 min-w-0" style={{ fontSize: 12, color: 'var(--ink2)', overflowWrap: 'anywhere' }}>
            <b>{z.neuer_name}</b>{' · '}{(z.ziel_pfad ?? '').split('/').join(' › ')}{z.bearbeitet_am ? ` · übernommen am ${deDatumZeit(z.bearbeitet_am)}` : ''}
          </span>
          {z.gdrive_file_id && <ImmoButton groesse={44} icon={Archive} onClick={() => aktionen.onOeffnen(z)}>Im Aktenschrank öffnen</ImmoButton>}
        </div>
      )}

      {z.status === 'abgelehnt' && (
        <div className="flex items-center justify-end flex-wrap" style={{ gap: 10 }}>
          {z.ablehnungsgrund && <span className="flex-1 min-w-0" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink2)' }}>Nicht übernommen: {z.ablehnungsgrund}</span>}
          {fehler && <span className="flex-1" style={{ fontSize: 12, color: 'var(--bad)' }}>{fehler}</span>}
          <ImmoButton groesse={44} icon={ArrowCounterClockwise} disabled={sperre} onClick={() => void fuehreAus(() => aktionen.onZurueck(z))}>Zurück in die Prüfung</ImmoButton>
        </div>
      )}
    </article>
  );
}
